import { type AbstractMesh, type Scene, Vector3, VertexBuffer } from "@babylonjs/core";
import type { WalkWorld } from "./walker";

interface Cached { flag: number; tris: Float32Array }

/**
 * The walker's view of the scene (spec 2m §3.3): world-space triangles of every collider near the player.
 * A mesh collides when checkCollisions is on, so collider modes and the trap guard work as before; terrain
 * is left out, the walker reads its height instead. Each mesh's triangles are cached until its world matrix
 * changes (moved, or carried by an animated parent).
 */
export class CollisionWorld implements WalkWorld {
  private cache = new Map<number, Cached>();
  private buf = new Float32Array(9 * 4096);

  constructor(private readonly scene: Scene, private readonly height: (x: number, z: number) => number) {}

  terrain(x: number, z: number): number { return this.height(x, z); }

  /** Triangles that may reach into the box; the returned array is reused by the next call. */
  triangles(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number): Float32Array {
    let n = 0;
    for (const m of this.scene.meshes) {
      if (!m.checkCollisions || !m.isEnabled() || m.metadata?.terrain === true) continue;
      m.computeWorldMatrix();
      const bb = m.getBoundingInfo().boundingBox, lo = bb.minimumWorld, hi = bb.maximumWorld;
      if (hi.x < minX || lo.x > maxX || hi.y < minY || lo.y > maxY || hi.z < minZ || lo.z > maxZ) continue;
      const tris = this.worldTriangles(m);
      for (let t = 0; t < tris.length; t += 9) {
        if (Math.max(tris[t]!, tris[t + 3]!, tris[t + 6]!) < minX || Math.min(tris[t]!, tris[t + 3]!, tris[t + 6]!) > maxX) continue;
        if (Math.max(tris[t + 1]!, tris[t + 4]!, tris[t + 7]!) < minY || Math.min(tris[t + 1]!, tris[t + 4]!, tris[t + 7]!) > maxY) continue;
        if (Math.max(tris[t + 2]!, tris[t + 5]!, tris[t + 8]!) < minZ || Math.min(tris[t + 2]!, tris[t + 5]!, tris[t + 8]!) > maxZ) continue;
        if (n + 9 > this.buf.length) { const b = new Float32Array(this.buf.length * 2); b.set(this.buf); this.buf = b; }
        this.buf.set(tris.subarray(t, t + 9), n);
        n += 9;
      }
    }
    return this.buf.subarray(0, n);
  }

  /** How many meshes hold cached triangles (tests). */
  cachedMeshes(): number { return this.cache.size; }

  private worldTriangles(m: AbstractMesh): Float32Array {
    const world = m.getWorldMatrix();
    const hit = this.cache.get(m.uniqueId);
    if (hit && hit.flag === world.updateFlag) return hit.tris;
    if (!hit) m.onDisposeObservable.addOnce(() => this.cache.delete(m.uniqueId));
    const pos = m.getVerticesData(VertexBuffer.PositionKind);
    const idx = m.getIndices();
    const count = pos ? (idx ? idx.length : pos.length / 3) : 0;
    const tris = new Float32Array(Math.floor(count / 3) * 9);
    const v = new Vector3();
    for (let i = 0; i < tris.length / 3; i++) {
      const k = idx ? idx[i]! : i;
      Vector3.TransformCoordinatesFromFloatsToRef(pos![k * 3]!, pos![k * 3 + 1]!, pos![k * 3 + 2]!, world, v);
      tris[i * 3] = v.x; tris[i * 3 + 1] = v.y; tris[i * 3 + 2] = v.z;
    }
    this.cache.set(m.uniqueId, { flag: world.updateFlag, tris });
    return tris;
  }
}
